/* sb-dc-handle.js - what a product is, and what a design is for.

   Two readers, kept in one file because they use the same vocabulary:

     __dcHandle(productHandle)  -> what the customer is buying
     __dcTplKind(templateRow)   -> what a design in dc_templates is for

   PRODUCTS. The store sells the same few things in dozens of packaged
   handles: 8x8-step-and-repeat-banner-with-stand, 20x8-fabric-step-repeat-
   banner, 12x8-step-and-repeat-banner-with-stand-and-red-carpet. The design
   center catalog has 15 products and the templates page has a handful of
   hand-written entries, so every packaged handle used to fall through as
   "unknown" and the grid stayed unfiltered. Listing them all would go stale
   the next time someone adds 24'x8', so this reads the handle:

     __dcHandle('8x8-step-and-repeat-banner-with-stand')
       -> { w:8, h:8, repeat:true, stand:true, templates:true, fits:null,
            dcHandle:'custom-step-and-repeat-banner' }

   `templates` is the families we keep designs for: step & repeat walls, tall
   stands, plain vinyl banners and the three sticker shapes. Vinyl and fabric
   step & repeat share one set; retractable and X-banner share another.
   snippets/sb-dc-cta.liquid gates its "Browse Design Templates" box on the
   same rules, written in Liquid - keep the two in step.

   dcHandle is the nearest thing the design center actually sells. NOTE that
   for "with stand" and "with red carpet" handles it is the banner-only
   product: the design center cannot price a stand, so the cart line will be
   the banner. See the note in sb-dc-nostart.js.

   DESIGNS. dc_templates stores a name, a theme category (Political, Holiday,
   ...), a shape (wide / square / tall) and nothing that says what product the
   design belongs on. Shape alone is not a category: a step & repeat wall and
   a bumper sticker are both "wide", which is how repeat walls ended up in the
   custom vinyl banner grid. So __dcTplKind reads the NAME.

   That means a repeat design has to be named "Step and Repeat ..." /
   "step-repeat-..." / "Checkerboard ..." / "... Logo Wall", and sticker artwork has
   to say "sticker" or "bumper". Both separators are in the library already
   ("Step and Repeat - Classic 8x8" and "step-repeat-wedding-heart-8x8"),
   hence the loose separator class - an earlier version required the "and" and
   silently missed 39 hyphenated designs.

   2026-09-03 - the real fix arrived: dc_templates has a products column and
   the admin panel has a picker for it. __dcFamily below is the reader for
   that column. Naming is now the FALLBACK, not the rule: 253 of 344 rows are
   still untagged, so tplKind stays until they are filled in. Where both exist
   they agree - all 91 tagged rows classify the same either way - so this is a
   safety net, not a second opinion. */
(function () {
  "use strict";

  /* NAME_GUESS switch (2026-09-03). tplKind()/tplKinds() used to GUESS a
     product from the file name, to cover the 253 rows with no products tag.
     Most rows are tagged now, so the guess is off. When off, an untagged
     template shows on no product - that is the to-do list. Set true to
     restore the old name-guessing. Two callers read this flag:
     assets/sb-dc-tplprod.js and sections/sb-dc-templates.liquid. Fill in the
     tags before turning it off, or those templates vanish. */
  var NAME_GUESS = false;
  window.__dcNameGuess = NAME_GUESS;

  var REPEAT = /step[\s_-]*(?:and|&|n)?[\s_-]*repeat|logo[\s_-]*wall|checkerboard|repeat[\s_-]*pattern/i;
  var TALL   = /retractable|x[-_ ]?banner|roll[-_ ]?up|pull[-_ ]?up/;
  var STICK  = /sticker|decal/;
  var STICKER_ART = /sticker|bumper|kiss[\s_-]*cut/i;

  /* 2026-09-10: the pop-up display STANDS. Three handles - pop-up-display-stand,
     pop-up-stand-8x8, pop-up-stand-8x10 - carry neither "backdrop" nor "banner"
     in the handle, so every test below missed them: family() returned [] and the
     templates page showed all 1,700-odd designs unfiltered, with no size box.
     Reported 2026-09-10 and confirmed live. They take the same printed fabric
     graphic as the pop-up display backdrop, so that is the family they get.
     Written as its own constant because "pop up" alone would also catch
     pop-up-retractable-banner, which is a different stand with its own designs. */
  var POPUP_STAND = /pop[-_ ]?up[-_ ]?(?:display[-_ ]?)?stand|display[-_ ]?stand/;

  /* ---- products -------------------------------------------------------- */

  function parse(handle) {
    var h = String(handle || "").toLowerCase();
    if (!h) return null;

    var m = /(?:^|[^0-9])(\d{1,3})x(\d{1,3})(?![0-9])/.exec(h);
    var w = m ? +m[1] : 0, ht = m ? +m[2] : 0;

    var o = {
      handle: h,
      w: w || 0,
      h: ht || 0,
      // "red-carpet-backdrop" is a carpet, not a repeat wall. Only the
      // trailing "...-and-red-carpet" packages are.
      // 2026-09-30 (방안 2, owner): «Repeat Banners» and «Red Carpet Backdrops» are step & repeat products
      // (same variants and prices as Custom Step and Repeat) - they used to open as Custom Vinyl Banners 8x4.
      repeat: (REPEAT.test(h) || /backdrop/.test(h) || POPUP_STAND.test(h) || /^repeat[-_]banners?$/.test(h))
        && !/^red[-_ ]carpet(?![-_ ]backdrops?$)/.test(h),
      tall: TALL.test(h),
      sticker: STICK.test(h),
      fabric: /fabric/.test(h),
      mesh: /mesh/.test(h),
      stand: /with[-_ ]stand/.test(h),
      carpet: /red[-_ ]carpet/.test(h),
      templates: false,
      fits: null,
      dcHandle: ""
    };

    /* 2026-09-08: mesh banners and window decals / adhesive banners joined the
       list. They have no designs of their own - they borrow the custom vinyl
       banner set through SHARE below - but as far as "is there a templates box
       on this product page" goes the answer is now yes. snippets/sb-dc-cta.liquid
       has the same two words in Liquid; change one, change the other.
       2026-09-09: yard signs and the flag shapes now keep their own designs.
       2026-09-12: window decals no longer borrow - see BORROW_VINYL_DECAL. */
    o.templates = o.repeat || o.tall
      || /custom-vinyl-banner/.test(h)
      || /mesh/.test(h)
      || /window|decal|adhesive/.test(h)
      || /feather|teardrop|rectangle-flag|econo|yard-sign/.test(h)
      || /bumper-sticker|oval-sticker|round-sticker/.test(h);

    // Shape. Null for repeat walls on purpose - a pattern tiles onto any board.
    if (o.repeat) {
      o.fits = null;
    } else if (o.tall) {
      o.fits = ["tall"];
    } else if (o.sticker) {
      o.fits = /round/.test(h) ? ["square"] : ["wide"];
    } else if (/feather|teardrop|rectangle-flag|econo/.test(h)) {
      o.fits = ["tall"];             // flags are tall
    } else if (/yard-sign/.test(h)) {
      o.fits = ["wide"];             // 24x18 landscape
    } else if (w && ht) {
      o.fits = w > ht ? ["wide"] : (w < ht ? ["tall"] : ["square"]);
    } else if (/banner|flag/.test(h)) {
      o.fits = ["wide", "square"];   // a banner with no size in its handle
    }

    if (POPUP_STAND.test(h)) {
      // a stand on its own: the design that goes on it is the pop-up display
      // backdrop graphic, so that is what the design center should open
      o.dcHandle = "8x8-fabric-pop-up-display-backdrop-banner-only";
    } else if (/pop[-_ ]?up/.test(h) && /backdrop/.test(h)) {
      // 2026-09-15: pop-up display backdrops (with stand / red carpet) used to
      // fall into o.repeat below and open Fabric Step & Repeat. They open the
      // banner-only product of their size; no size in the handle -> 8x8 (store's choice).
      o.dcHandle = (w === 10 && ht === 8)
        ? "10x8-fabric-pop-up-display-backdrop-banner-only"
        : "8x8-fabric-pop-up-display-backdrop-banner-only";
    } else if (o.repeat) {
      o.dcHandle = o.fabric ? "fabric-step-repeat-banners" : "custom-step-and-repeat-banner";
    } else if (/retractable|roll[-_ ]?up|pull[-_ ]?up/.test(h)) {
      o.dcHandle = "pop-up-retractable-banner";
    } else if (/double[-_ ]?sided/.test(h) && /x[-_ ]?banner/.test(h)) {
      o.dcHandle = "double-sided-x-banner";
    } else if (/x[-_ ]?banner/.test(h)) {
      o.dcHandle = "x-banner";
    /* 2026-09-16: see-thru window decals. The product handle is
       "see-thru-decal"; STICK at the top answers to "decal", so o.sticker was
       true, the sticker branch below ran through its four shapes without a
       match and left dcHandle empty. An empty dcHandle is "unknown product"
       to sb-dc-nostart.js, which then starts on the design center's own
       default - Custom Vinyl Banners at 8ft x 4ft, which is what the store
       saw. Reported 2026-09-16.

       These sit BEFORE the shapes on purpose, and deliberately do not
       include a bare "decal": round / oval / kiss-cut / bumper carry none of
       these words, so they still reach their own branch, and a handle whose
       only decal word is "decal" still lands on the catch below.

       See-thru is kept apart from window / adhesive because it is its own
       product at its own price - $2.99/sqft against $2.75 - and its card is
       spliced in by assets/sb-dc-unhide.js. If that card is ever dropped,
       point this branch at "window-decals" instead, or the product falls
       back through to Custom Vinyl Banners. */
    } else if (/see[-_ ]?thru|see[-_ ]?through/.test(h)) {
      o.dcHandle = "see-thru-decal";
    } else if (/window|adhesive/.test(h)) {
      o.dcHandle = "window-decals";
    } else if (o.sticker) {
      o.dcHandle = /round/.test(h) ? "round-stickers"
                 : /oval/.test(h) ? "oval-stickers"
                 : /kiss/.test(h) ? "kiss-cut-stickers"
                 : /bumper/.test(h) ? "bumper-stickers" : "";
    } else if (/window|decal|adhesive/.test(h)) {
      o.dcHandle = "window-decals";
    } else if (/feather|teardrop|rectangle-flag|econo-feather|yard-sign/.test(h)) {
      // opened by its own handle in the design center (sb-dc-yardflag.js)
      o.dcHandle = h;
    } else if (o.mesh) {
      o.dcHandle = "mesh-banners";
    } else if (/banner|flag/.test(h)) {
      o.dcHandle = o.fabric ? "fabric-banners" : "custom-vinyl-banners";
    }

    return o;
  }

  /* ---- designs --------------------------------------------------------- */

  // Takes a dc_templates row. Returns 'repeat' | 'tall' | 'sticker' | 'banner'.
  function tplKind(t) {
    if (!t) return "banner";
    var n = String(t.name || "");
    if (REPEAT.test(n)) return "repeat";
    var fit = t.fit || [];
    if (fit.indexOf("tall") >= 0) return "tall";      // roll-ups and X-banners
    if (STICKER_ART.test(n)) return "sticker";
    return "banner";
  }

  /* ---- product families ------------------------------------------------

     2026-09-03. The admin panel grew a target-product chip picker and writes the
     answer to dc_templates.products, in its own vocabulary - one key per
     product family, the same twelve chips the person clicks. Nothing on the
     site read that column, so a design tagged "Retractable" still turned up
     on a vinyl banner and the picker looked broken. This is the reader that
     was missing; the keys here and the FAMILIES list in the admin panel are
     the same list written twice - change one, change the other.

       __dcFamily('8x8-step-and-repeat-banner-with-stand')
         -> ['step-repeat', 'popup-backdrop']

     Exact match, with the sharing this file already described in prose:
     vinyl and fabric step & repeat are one set (they fall out of parse()
     together), retractable and X-banner are another, and a pop-up display
     backdrop prints the same repeat artwork as a step & repeat wall. SHARE
     is the one line to edit if a family should start borrowing another's
     designs. An unrecognised handle returns [] and nothing gets filtered. */
  /* 2026-09-03: pop-up backdrop borrow is OFF (tags won, not the pair);
     the 107 step-and-repeat rows were tagged Pop-Up Display Backdrop instead.
     Set BORROW_BACKDROP true to restore. Retractable<->X-banner borrow stays
     ON (owner's call) - both are tall stands sharing one set of tall designs.
     Set BORROW_XBANNER false to turn it off. */
  var BORROW_BACKDROP = false;
  var BORROW_XBANNER  = true;

  /* 2026-09-08: mesh banner and window decal / adhesive banner SHARED the
     custom vinyl banner set (same wide artwork, different material). One-way:
     the vinyl screen does not get mesh/decal-only designs.

     2026-09-12 - the borrow is now per product, because the two are not the
     same case. Reported: "Mesh, Fabric and Window Decals all show the vinyl
     pool; a decal customer wants small hours/sale/logo decals and gets 8x4
     political banners."

       MESH  keeps borrowing. A mesh banner IS a banner - same shapes, same
             sizes, same artwork - so the vinyl designs genuinely fit, and
             mesh has only 68 of its own.
       DECAL stops borrowing. A window decal is a different object at a
             different size, and a 8x4 campaign banner is never the right
             answer for one. Measured: window-decal has 11 designs of its own,
             so the grid is thin but relevant rather than full and wrong.

     Fabric banners were never in SHARE and already show their own 68.

     Eleven designs is not many. The real fix is the store tagging more
     artwork to Window Decals in the admin panel's target-product picker - at
     which point nothing here needs changing. Set BORROW_VINYL_DECAL true to
     put the old behaviour back; if you do, no other file needs touching, as
     the product still has designs either way. */
  var BORROW_VINYL_MESH  = true;
  var BORROW_VINYL_DECAL = false;

  var SHARE = {
    "step-repeat":    BORROW_BACKDROP ? ["step-repeat", "popup-backdrop"] : ["step-repeat"],
    "popup-backdrop": BORROW_BACKDROP ? ["popup-backdrop", "step-repeat"] : ["popup-backdrop"],
    "retractable":    BORROW_XBANNER  ? ["retractable", "x-banner"] : ["retractable"],
    "x-banner":       BORROW_XBANNER  ? ["x-banner", "retractable"] : ["x-banner"],
    "mesh-banner":    BORROW_VINYL_MESH  ? ["mesh-banner", "vinyl-banner"] : ["mesh-banner"],
    "window-decal":   BORROW_VINYL_DECAL ? ["window-decal", "vinyl-banner"] : ["window-decal"]
  };

  function family(handle) {
    var h = String(handle || "").toLowerCase();
    var p = parse(h);
    if (!p) return [];
    var k = "";
    // Backdrop first: parse() calls every backdrop a repeat wall, which is
    // right for artwork but loses the pop-up display as its own chip.
    if (/backdrop/.test(h) && /pop[-_ ]?up|display/.test(h)) k = "popup-backdrop";
    // 2026-09-10: the stands themselves - see POPUP_STAND at the top. Before
    // p.repeat so they land on the display chip, not the step & repeat one.
    else if (POPUP_STAND.test(h)) k = "popup-backdrop";
    else if (p.repeat) k = "step-repeat";
    else if (/retractable|roll[-_ ]?up|pull[-_ ]?up/.test(h)) k = "retractable";
    else if (/x[-_ ]?banner/.test(h)) k = "x-banner";
    else if (p.sticker && /round/.test(h)) k = "round-sticker";
    else if (p.sticker && /oval/.test(h)) k = "oval-sticker";
    else if (p.sticker && /kiss/.test(h)) k = "kiss-cut-sticker";
    else if (p.sticker && /bumper/.test(h)) k = "bumper-sticker";
    // Window decals answer STICK too ("decal"), so they have to come after
    // the four shapes and before the banners.
    else if (/window|decal|adhesive/.test(h)) k = "window-decal";
    // 2026-09-09: the flag shapes and the yard sign are their own families,
    // each with its own designs. Before the /banner|flag/ catch-all, which
    // would otherwise sweep every flag into vinyl-banner.
    else if (/feather[-_ ]?angled|angled[-_ ]?feather/.test(h)) k = "feather-angled";
    else if (/feather[-_ ]?convex|convex[-_ ]?feather/.test(h)) k = "feather-convex";
    else if (/teardrop/.test(h)) k = "teardrop-flag";
    else if (/rectangle[-_ ]?flag/.test(h)) k = "rectangle-flag";
    else if (/econo[-_ ]?feather|econo[-_ ]?flag/.test(h)) k = "econo-feather";
    else if (/yard[-_ ]?sign/.test(h)) k = "yard-sign";
    else if (p.mesh) k = "mesh-banner";
    else if (p.fabric) k = "fabric-banner";
    else if (/banner|flag/.test(h)) k = "vinyl-banner";
    if (!k) return [];
    return SHARE[k] || [k];
  }

  /* Which design KINDS belong on a product - the fallback for the 253 of 344
     templates nobody has tagged yet. The templates page worked this out
     inline; it lives here now so the design center's own panel can read the
     same answer instead of showing everything that happens to be the right
     shape. Null means "no opinion". */
  function tplKinds(handle) {
    var h = String(handle || "").toLowerCase();
    var p = parse(h);
    if (!p) return null;
    if (p.repeat) return ["repeat"];
    if (p.tall) return ["tall"];
    if (/feather|teardrop|rectangle-flag|econo/.test(h)) return ["tall"];
    if (/yard-sign/.test(h)) return ["banner"];
    // 2026-09-16: see-thru joins these, for the reason given in parse().
    if (/see[-_ ]?thru|see[-_ ]?through|window|adhesive/.test(h)) return ["banner"];
    // Sticker artwork is thin on the ground, so banner designs ride along.
    if (p.sticker) return ["sticker", "banner"];
    return ["banner"];
  }

  window.__dcHandle = parse;
  window.__dcTplKind = tplKind;
  window.__dcFamily = family;
  window.__dcTplKinds = tplKinds;
})();
